import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck,
  ScanSearch,
  Radar,
  GaugeCircle,
  FileCheck2,
  Plug,
  ArrowRight,
  Check,
  Sparkles,
  Menu,
  X,
  Twitter,
  Linkedin,
  Star,
  Quote,
  ChevronRight,
  MessageSquare,
  GitCompare,
  History,
  Languages,
  FileSignature,
  BookOpen,
  Share2,
  Bell,
  AlertTriangle,
  FileText,
  TrendingUp,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import WaitlistForm from "@/components/landing/WaitlistForm";
import RiskScoreTool from "@/components/landing/RiskScoreTool";

const HERO_IMAGE = "/landing-hero.png";

const TESTIMONIALS = [
  {
    name: "Sarah Mitchell",
    role: "Founder, NorthLine Commerce",
    quote:
      "AgentFlow flagged three GDPR exposures in our checkout policy on day one. The audit-ready report saved us a week of legal back-and-forth.",
    img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0fGVufDB8fHx8MTc3OTAzOTg4NXww&ixlib=rb-4.1.0&q=85",
  },
  {
    name: "David Park",
    role: "Operations Lead, Harbor SaaS",
    quote:
      "We finally have a single dashboard for SOC2, GDPR and CCPA risk. The weekly digest is the only compliance email I actually read.",
    img: "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0fGVufDB8fHx8MTc3OTAzOTg4NXww&ixlib=rb-4.1.0&q=85",
  },
  {
    name: "Mira Alvarez",
    role: "Compliance Manager, Bright Studio",
    quote:
      "Setup took 12 minutes. The AI agents already feel like an extra teammate scanning every contract before I sign it.",
    img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0fGVufDB8fHx8MTc3OTAzOTg4NXww&ixlib=rb-4.1.0&q=85",
  },
];

const FAQS = [
  {
    q: "Which regulations does AgentFlow monitor?",
    a: "Out of the box: GDPR, CCPA, HIPAA, SOC2, PCI-DSS and FTC consumer protection rules. We also cover Spanish LOPDGDD, Brazilian LGPD, French CNIL guidance, and German DSGVO when scanning contracts in those languages. We continuously add jurisdictions and you can request custom frameworks on the Business plan.",
  },
  {
    q: "How does the AI agent actually scan documents?",
    a: "Upload PDFs, DOCX, or paste text — or import directly from Google Drive on Business. Our agent extracts clauses, compares them against a live regulation knowledge base, and produces a prioritized risk list with citation links and suggested fixes. Then ask follow-up questions in plain English with AI chat on the report (Pro).",
  },
  {
    q: "What languages are supported?",
    a: "Five today: English, Spanish, Portuguese, French, and German. We auto-detect the contract's language and cite the right legal framework for that jurisdiction. Toggle between native report and one-click English translation on any scan.",
  },
  {
    q: "Can I track contracts across multiple revision rounds?",
    a: "Yes — that's exactly what Version History does. Upload v2, v3, v4 of the same contract and we automatically link them. The side-by-side risk diff shows you what changed, what got resolved, and which clauses got worse — so you can catch a counterparty quietly slipping in an adversarial edit during redline rounds.",
  },
  {
    q: "Is my data secure?",
    a: "Documents are encrypted in transit and at rest. Refresh tokens for Google Drive and Slack are Fernet-encrypted per user. We never train on your data, never send it to third-party model providers without your control, and offer regional storage (EU/US) on the Business plan.",
  },
  {
    q: "Do I need a compliance background to use it?",
    a: "No. AgentFlow is designed for SMB owners and operators. Every risk includes a plain-language explanation, a recommended fix written in human terms, and an optional AI chat where you can ask things like &quot;what does this mean for me?&quot; or &quot;draft a 1-line pushback I can send back.&quot;",
  },
  {
    q: "How does billing work?",
    a: "Free today, no credit card required. Upgrade to Pro ($39/mo) or Business ($149/mo) directly from your dashboard — payment is powered by Paddle (merchant of record, so VAT/sales tax is handled automatically). Cancel anytime, and annual billing saves ~17%.",
  },
];

const NAV_LINKS = [
  { label: "Risk Score", href: "#risk-tool" },
  { label: "Features", href: "#features" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(null);
  const [waitlist24h, setWaitlist24h] = useState(null);
  const [reportsCount, setReportsCount] = useState(null);
  const [reports24h, setReports24h] = useState(null);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    let cancelled = false;

    const fetchCounts = async () => {
      try {
        const [w, r] = await Promise.all([
          fetch(`${base}/api/waitlist/count`).then((res) =>
            res.ok ? res.json() : null
          ),
          fetch(`${base}/api/risk-score/count`).then((res) =>
            res.ok ? res.json() : null
          ),
        ]);
        if (cancelled) return;
        if (w && typeof w.count === "number") {
          setWaitlistCount(w.count);
          if (typeof w.last_24h === "number") setWaitlist24h(w.last_24h);
        }
        if (r && typeof r.count === "number") {
          setReportsCount(r.count);
          if (typeof r.last_24h === "number") setReports24h(r.last_24h);
        }
      } catch {
        /* ignore */
      }
    };

    fetchCounts();
    const id = setInterval(fetchCounts, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false);
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased selection:bg-blue-600 selection:text-white">
      {/* Header */}
      <header
        className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-200"
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <a
            href="#top"
            className="flex items-center gap-2"
            data-testid="brand-logo"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-md bg-blue-600 text-white shadow-sm">
              <ShieldCheck className="h-4 w-4" strokeWidth={2.5} />
            </span>
            <span className="font-display font-semibold tracking-tight text-slate-900 text-lg">
              AgentFlow
              <span className="text-blue-600">.</span>
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => scrollTo(l.href)}
                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                data-testid={`nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="/login"
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="header-signin"
            >
              Sign in
            </a>
            <Button
              onClick={() => (window.location.href = "/register")}
              className="rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm h-9 px-4 transition-colors duration-200"
              data-testid="header-cta-button"
            >
              Upload Contract
            </Button>
          </div>

          <button
            className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-700 hover:bg-slate-100"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white">
            <div className="px-6 py-4 flex flex-col gap-3">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => scrollTo(l.href)}
                  className="text-left text-base font-medium text-slate-700 hover:text-blue-600 py-1"
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </button>
              ))}
              <Button
                onClick={() => navigate("/register")}
                className="mt-2 w-full rounded-full bg-blue-600 hover:bg-blue-700 text-white transition-colors duration-200"
                data-testid="mobile-header-cta"
              >
                Upload Contract — Free
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero */}
      <section id="top" className="relative pt-32 pb-24 sm:pt-40 sm:pb-32 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-grid-slate fade-mask-bottom opacity-60" />
        <div className="absolute top-0 right-0 -z-10 h-[500px] w-[500px] rounded-full bg-blue-50/60 blur-3xl" />
        <div className="absolute top-32 left-0 -z-10 h-[400px] w-[400px] rounded-full bg-cyan-50/60 blur-3xl" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-xs font-medium text-slate-600 shadow-sm"
              data-testid="hero-badge"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-600" />
              </span>
              <span className="uppercase tracking-[0.18em] text-[10px] text-blue-700">
                Used by founders and small businesses
              </span>
              <span className="text-slate-500" data-testid="hero-badge-waitlist-count">
                ·{" "}
                {waitlistCount === null ? (
                  <span className="inline-block h-3 w-6 align-middle bg-slate-200 rounded animate-pulse" />
                ) : (
                  <strong className="font-semibold text-slate-700">
                    {waitlistCount.toLocaleString()}
                  </strong>
                )}{" "}
                onboarded
              </span>
            </div>

            <h1 className="mt-6 font-display font-semibold text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tighter text-slate-900">
              Know before
              <br />
              <span className="italic font-light text-slate-500">you</span>{" "}
              <span className="text-blue-600">sign.</span>
            </h1>

            <p className="mt-7 text-lg leading-relaxed text-slate-600 max-w-xl">
              AI that scans contracts for{" "}
              <span className="font-semibold text-slate-900">hidden risks</span>,{" "}
              <span className="font-semibold text-slate-900">unfair terms</span>, and{" "}
              <span className="font-semibold text-slate-900">dangerous clauses</span>{" "}
              in seconds — before they cost you.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <Button
                onClick={() => navigate("/register")}
                className="rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25 h-12 px-7 text-sm font-semibold transition-transform duration-200 hover:-translate-y-0.5"
                data-testid="hero-cta-button"
              >
                Upload Your First Contract — Free
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                onClick={() => scrollTo("#risk-tool")}
                className="rounded-full h-12 px-6 text-sm font-semibold border-slate-300 hover:bg-slate-50 transition-colors duration-200"
                data-testid="hero-secondary-cta"
              >
                <Sparkles className="mr-2 h-4 w-4 text-blue-600" />
                Try Free Risk Score
              </Button>
            </div>

            {/* Live social-proof pill — three tiers:
                  1. Healthy 24h volume (≥10) → "N reports in the last 24 hours" (urgency framing)
                  2. Otherwise healthy lifetime (≥10) → "N risk reports generated" (cumulative framing)
                  3. Brand-new product → hide entirely (don't make page look slow).
                Polls every 30s via the existing fetchCounts effect. */}
            {(() => {
              if (reports24h !== null && reports24h >= 10) {
                return (
                  <div
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-emerald-50/70 px-3 py-1.5 text-xs font-medium text-emerald-800 ring-1 ring-inset ring-emerald-200"
                    data-testid="hero-live-counter"
                    data-counter-mode="24h"
                  >
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                    </span>
                    <strong className="font-semibold tabular-nums" data-testid="hero-live-counter-value">
                      {reports24h.toLocaleString()}
                    </strong>
                    <span className="text-emerald-700/80">risk reports generated in the last 24 hours</span>
                  </div>
                );
              }
              if (reportsCount !== null && reportsCount >= 10) {
                return (
                  <div
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-blue-50/70 px-3 py-1.5 text-xs font-medium text-blue-800 ring-1 ring-inset ring-blue-200"
                    data-testid="hero-live-counter"
                    data-counter-mode="lifetime"
                  >
                    <Sparkles className="h-3 w-3 text-blue-600" />
                    <strong className="font-semibold tabular-nums" data-testid="hero-live-counter-value">
                      {reportsCount.toLocaleString()}
                    </strong>
                    <span className="text-blue-700/80">risk reports analyzed since launch</span>
                  </div>
                );
              }
              return null;
            })()}

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-600" strokeWidth={3} />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-600" strokeWidth={3} />
                <span>5 free scans / month</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-600" strokeWidth={3} />
                <span>Results in ~10 seconds</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <ScannedContractMock />
          </div>
        </div>

        {/* Live counters strip */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-20">
          <div
            className="grid grid-cols-1 sm:grid-cols-2 rounded-2xl border border-slate-200 bg-white/70 backdrop-blur-md overflow-hidden shadow-sm"
            data-testid="live-counters"
          >
            <CounterTile
              testId="counter-waitlist"
              label="Founders in early access"
              value={waitlistCount}
              recent={waitlist24h}
              recentLabel={(n) =>
                n === 1
                  ? "1 founder onboarded in the last 24 hours"
                  : `${n.toLocaleString()} founders onboarded in the last 24 hours`
              }
              accent="blue"
              note="Real signups from the form below — updates live."
            />
            <CounterTile
              testId="counter-reports"
              label="AI risk reports generated"
              value={reportsCount}
              recent={reports24h}
              recentLabel={(n) =>
                n === 1
                  ? "1 risk report generated in the last 24 hours"
                  : `${n.toLocaleString()} risk reports generated in the last 24 hours`
              }
              accent="cyan"
              note="Free clauses analyzed since launch."
              divider
            />
          </div>
        </div>

        {/* Trusted by row */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-20">
          <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">
            Trusted by operators at
          </p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 items-center gap-x-8 gap-y-6">
            {["NORTHLINE", "HARBOR", "BRIGHT STUDIO", "OAKMERE", "FIDELIO", "VANTA SMB"].map(
              (name) => (
                <div
                  key={name}
                  className="text-slate-400 hover:text-slate-700 transition-colors duration-200 font-display font-semibold tracking-[0.18em] text-sm text-center"
                  data-testid={`logo-${name.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {name}
                </div>
              )
            )}
          </div>
        </div>
      </section>

      {/* Social Proof Band — dark surface, breaks page flow */}
      <SocialProofBand reportsCount={reportsCount} reports24h={reports24h} />

      {/* Scary Contracts Showcase — real-looking bad clauses that AgentFlow flags */}
      <ScaryContractsShowcase onCtaClick={() => navigate("/register")} />

      {/* AI Risk Score Tool */}
      <RiskScoreTool onJoinWaitlist={() => navigate("/register")} />

      {/* Features Bento */}
      <section id="features" className="relative py-24 sm:py-32 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">
              Features
            </p>
            <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-tight">
              An always-on compliance{" "}
              <span className="text-blue-600">team in a tab</span>.
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              Twelve AI-driven workflows that replace the spreadsheets, calendar
              reminders, and paid consultants you've been duct-taping together.
              Built for SMBs that negotiate contracts and ship products in
              English, Spanish, Portuguese, French or German.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Risk Dashboard - WIDE */}
            <FeatureCard
              wide
              testId="feature-risk-dashboard"
              icon={<GaugeCircle className="h-5 w-5" />}
              label="Live"
              title="Risk dashboard with scores & fixes"
              body="A single command center that ranks every issue from critical to informational, with one-click remediations written for humans."
            >
              <div className="mt-6 rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                  <span>Overall risk score</span>
                  <span className="font-mono text-slate-900">24 / 100</span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full w-[24%] rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600" />
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                  {[
                    { l: "Critical", v: 1, c: "text-rose-600 bg-rose-50" },
                    { l: "Medium", v: 4, c: "text-amber-600 bg-amber-50" },
                    { l: "Low", v: 9, c: "text-emerald-600 bg-emerald-50" },
                  ].map((s) => (
                    <div key={s.l} className={`rounded-md py-2 ${s.c}`}>
                      <div className="text-lg font-semibold leading-none">{s.v}</div>
                      <div className="text-[10px] uppercase tracking-wider mt-1">
                        {s.l}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </FeatureCard>

            <FeatureCard
              testId="feature-document-scan"
              icon={<ScanSearch className="h-5 w-5" />}
              title="AI document scanning"
              body="Drop in policies, contracts or terms of service. Agents flag risky clauses with citations and suggested rewrites."
            />

            <FeatureCard
              testId="feature-monitoring"
              icon={<Radar className="h-5 w-5" />}
              title="Daily regulatory monitoring"
              body="Autonomous agents watch GDPR, CCPA, HIPAA, SOC2 and more — pinging you only when a change actually affects you."
            />

            <FeatureCard
              testId="feature-audit-reports"
              icon={<FileCheck2 className="h-5 w-5" />}
              title="Audit-ready reports"
              body="Generate beautifully formatted PDFs for auditors, investors or insurance — branded with your logo in one click."
            />

            <FeatureCard
              wide
              testId="feature-integrations"
              icon={<Plug className="h-5 w-5" />}
              label="Live"
              title="Plug into the tools you already use"
              body="Connect Google Drive to import contracts straight from your folders. Pipe critical-risk alerts into your team's Slack channel. We sync read-only, encrypt every refresh token at rest with per-user Fernet keys, and never train models on your data."
            >
              <div className="mt-6 flex flex-wrap items-center gap-2">
                {[
                  { name: "Google Drive", live: true },
                  { name: "Slack", live: true },
                  { name: "Email alerts", live: true },
                  { name: "Resend digests", live: true },
                  { name: "Webhook", live: false },
                  { name: "Gmail", live: false },
                ].map((i) => (
                  <span
                    key={i.name}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-3 py-1.5 text-xs text-slate-700 shadow-sm"
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${i.live ? "bg-emerald-500" : "bg-slate-300"}`} />
                    {i.name}
                    {!i.live && <span className="text-[10px] uppercase tracking-wider text-slate-400">soon</span>}
                  </span>
                ))}
              </div>
            </FeatureCard>

            {/* ---- New shipped capabilities ------------------------- */}
            <FeatureCard
              testId="feature-redline"
              icon={<FileSignature className="h-5 w-5" />}
              label="New"
              title="Suggested redline with tracked changes"
              body="One click drafts a counter-version of any contract with rationale per change, severity tagging, and a downloadable Word .docx with native tracked changes — ready to send back to the counterparty."
            />

            <FeatureCard
              testId="feature-chat"
              icon={<MessageSquare className="h-5 w-5" />}
              label="Pro"
              title="AI chat on every report"
              body="Ask plain-English follow-ups about any finding — &quot;what does this clause actually mean for me?&quot;, &quot;draft a 1-line pushback I can send back&quot;. Persistent per-scan history, scoped to your workspace."
            />

            <FeatureCard
              testId="feature-version-history"
              icon={<History className="h-5 w-5" />}
              label="New"
              title="Version history & risk diff"
              body="Upload v2, v3, v4 — we automatically link them. Side-by-side diff of risks introduced, resolved, or shifted in severity. Inline sparkline on every doc shows whether the contract is getting safer or riskier across rounds."
            />

            <FeatureCard
              testId="feature-compare"
              icon={<GitCompare className="h-5 w-5" />}
              title="Head-to-head contract comparison"
              body="Pick 2–5 drafts (vendor proposals, NDAs, etc.) and let AI rank them by safety, surface key topic-by-topic differences, and tell you which to sign and why."
            />

            <FeatureCard
              testId="feature-multilingual"
              icon={<Languages className="h-5 w-5" />}
              label="5 languages"
              title="Multilingual scanning, native legal context"
              body="English, Spanish, Portuguese, French, German. We auto-detect the contract's language and cite the right legal frameworks (LOPDGDD/RGPD for Spain, LGPD for Brazil, CNIL for France, DSGVO for Germany). One-click translation toggle between report views."
            />

            <FeatureCard
              testId="feature-scorecard"
              icon={<Share2 className="h-5 w-5" />}
              label="Pro"
              title="Compliance Scorecard PDF & public sharing"
              body="Beautiful one-page scorecards you can email to procurement, auditors, or counterparties. Optional revokable public share link with email-gate so you see exactly who opened it."
            />

            <FeatureCard
              testId="feature-templates"
              icon={<BookOpen className="h-5 w-5" />}
              title="Safe Templates Library"
              body="Lawyer-vetted starting points for NDAs, consulting agreements, vendor MSAs, partnerships, employment offers, and B2B service contracts — free to copy or download in markdown."
            />

            <FeatureCard
              testId="feature-notifications"
              icon={<Bell className="h-5 w-5" />}
              title="Notification Center with retry"
              body="Granular control over every alert: email digests, Slack pings, in-app bells. One-click retry on any failed delivery, full activity log, opt-out per channel — without losing the always-on alerts that matter."
            />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">
              Pricing
            </p>
            <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-tight">
              Simple plans for{" "}
              <span className="text-blue-600">every stage</span> of compliance.
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              Start free — no card required. Upgrade to Pro or Business anytime
              from your dashboard. Cancel anytime, annual billing saves ~17%.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            <PricingCard
              testId="pricing-free"
              name="Free"
              price="$0"
              cadence="forever"
              description="For solo operators kicking the tires."
              features={[
                "5 document scans / month",
                "1 workspace",
                "Multilingual scanning (5 languages)",
                "Suggested redlines",
                "Head-to-head contract comparison",
                "Safe Templates Library",
              ]}
              ctaLabel="Get started free"
              ctaTestId="pricing-free-cta"
              onClick={() => navigate("/register")}
            />

            <PricingCard
              featured
              badge="Most popular"
              testId="pricing-pro"
              name="Pro"
              price="$39"
              cadence="per month"
              description="For growing SMBs who need confidence."
              features={[
                "100 scans / month, 3 workspaces",
                "Everything in Free, plus:",
                "AI chat on every report (300 msgs / month)",
                "Compliance Scorecard PDFs",
                "Public share links with email-gate",
                "Word .docx tracked-changes export",
                "Version history & risk diff",
                "Daily/weekly regulatory monitoring",
                "Priority email support",
              ]}
              ctaLabel="Start free — upgrade anytime"
              ctaTestId="pricing-pro-cta"
              onClick={() => navigate("/register")}
            />

            <PricingCard
              badge="For teams"
              testId="pricing-business"
              name="Business"
              price="$149"
              cadence="per month"
              description="For teams that scale audit & compliance ops."
              features={[
                "1,000 scans / month",
                "Unlimited workspaces",
                "Everything in Pro, plus:",
                "AI chat (2,000 msgs / month)",
                "Slack integration (live)",
                "Google Drive import (live)",
                "Recipients dashboard & view tracking",
                "Priority support (SLA)",
                "Custom frameworks on request",
              ]}
              ctaLabel="Start free — upgrade anytime"
              ctaTestId="pricing-business-cta"
              onClick={() => navigate("/register")}
            />
          </div>

          <p className="mt-6 text-center text-xs text-slate-500">
            Start free · Upgrade anytime · Cancel anytime · Annual billing
            saves ~17% · Secure payments via Paddle
          </p>
        </div>
      </section>

      {/* Early Access */}
      <section id="waitlist" className="py-24 sm:py-32 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">
              Request Access
            </p>
            <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-tight">
              Apply for the early access cohort.
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              We're onboarding a small group of founders each week to keep
              support quality high. Request access below — most approvals
              land within 24 hours and the Free plan gives you 5 scans /
              month while you decide. Already approved?{" "}
              <a href="/register" className="text-blue-600 font-medium hover:underline">
                Skip to sign-up →
              </a>
            </p>

            {typeof waitlist24h === "number" && waitlist24h > 0 && (
              <div
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm"
                data-testid="waitlist-recent-pill"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-600" />
                </span>
                <span>
                  <strong className="font-semibold text-slate-900">
                    {waitlist24h.toLocaleString()}
                  </strong>{" "}
                  {waitlist24h === 1 ? "founder" : "founders"} onboarded in the last 24 hours
                </span>
              </div>
            )}

            <ul className="mt-8 space-y-3">
              {[
                "Free plan available immediately — no waiting",
                "Priority onboarding with a real human",
                "Upgrade to Pro or Business anytime from your dashboard",
                "Direct line to the founders — your feedback shapes the roadmap",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-white shrink-0">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-10">
              <WaitlistForm />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">
              What early users say
            </p>
            <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-tight">
              Operators who stopped losing sleep over compliance.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <figure
                key={t.name}
                className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex items-center gap-1 text-amber-500">
                  {[...Array(5)].map((_, idx) => (
                    <Star key={idx} className="h-4 w-4 fill-current" />
                  ))}
                </div>
                <Quote className="mt-4 h-6 w-6 text-blue-100" strokeWidth={1.5} />
                <blockquote className="mt-3 text-[15px] leading-relaxed text-slate-700">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-3">
                  <img
                    src={t.img}
                    alt={t.name}
                    className="h-10 w-10 rounded-full object-cover ring-2 ring-white shadow-sm"
                  />
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 sm:py-32 bg-slate-50 border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">
              FAQ
            </p>
            <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-tight">
              Frequently asked questions.
            </h2>
          </div>

          <Accordion type="single" collapsible className="mt-12 w-full" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
                <AccordionTrigger
                  className="text-left font-display font-medium text-base sm:text-lg text-slate-900 hover:no-underline py-5"
                  data-testid={`faq-trigger-${i}`}
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed text-[15px] pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 sm:py-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-slate-900 px-8 py-16 sm:px-16 sm:py-20">
            <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />
            <div className="absolute -bottom-32 -left-12 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />
            <div className="relative max-w-3xl">
              <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
                Let an AI agent worry about regulations.{" "}
                <span className="text-cyan-300">You worry about growth.</span>
              </h2>
              <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">
                Join{" "}
                <strong
                  className="text-white font-semibold"
                  data-testid="final-cta-waitlist-count"
                >
                  {waitlistCount === null ? "…" : waitlistCount.toLocaleString()}
                </strong>{" "}
                founders already in the AgentFlow early-access cohort.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => navigate("/register")}
                  className="rounded-lg bg-white hover:bg-slate-100 text-slate-900 h-11 px-6 text-sm font-semibold"
                  data-testid="final-cta-button"
                >
                  Get Early Access — Free
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => scrollTo("#pricing")}
                  className="rounded-lg h-11 px-6 text-sm font-semibold text-white hover:bg-white/10"
                  data-testid="final-secondary-cta"
                >
                  View pricing
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-5 gap-8">
          <div className="col-span-2">
            <div className="flex items-center gap-2">
              <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-md bg-blue-600 text-white">
                <ShieldCheck className="h-4 w-4" strokeWidth={2.5} />
              </span>
              <span className="font-display font-semibold text-lg text-slate-900">
                AgentFlow<span className="text-blue-600">.</span>
              </span>
            </div>
            <p className="mt-4 text-sm text-slate-600 max-w-xs leading-relaxed">
              AI agents that watch regulations so SMBs don't have to. Built with
              care in San Francisco.
            </p>
            <div className="mt-5 flex items-center gap-3">
              <a
                href="https://x.com/AgentflowTool"
                target="_blank"
                rel="noopener noreferrer"
                className="h-9 w-9 rounded-md border border-slate-200 inline-flex items-center justify-center text-slate-600 hover:text-blue-600 hover:border-blue-200 transition-colors"
                aria-label="Follow AgentFlow on X"
                data-testid="footer-twitter"
              >
                <Twitter className="h-4 w-4" />
              </a>
              <a
                href="https://www.linkedin.com/in/agentflow-compliance"
                target="_blank"
                rel="noopener noreferrer"
                className="h-9 w-9 rounded-md border border-slate-200 inline-flex items-center justify-center text-slate-600 hover:text-blue-600 hover:border-blue-200 transition-colors"
                aria-label="Follow AgentFlow on LinkedIn"
                data-testid="footer-linkedin"
              >
                <Linkedin className="h-4 w-4" />
              </a>
            </div>
          </div>

          <FooterCol
            title="Product"
            links={[
              { to: "/", label: "Overview" },
              { to: "/pricing", label: "Pricing" },
              { to: "/register", label: "Start Free" },
              { to: "/login", label: "Sign in" },
            ]}
          />
          <FooterCol
            title="Company"
            links={[
              { href: "mailto:hello@agentflow.ventures", label: "Contact" },
              { href: "mailto:support@agentflow.ventures", label: "Support" },
            ]}
          />
          <FooterCol
            title="Legal"
            links={[
              { to: "/trust", label: "Trust & Security" },
              { to: "/terms", label: "Terms of Service" },
              { to: "/privacy", label: "Privacy Policy" },
              { to: "/refund", label: "Refund Policy" },
            ]}
          />
        </div>
        <div className="border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-slate-500">
              © {new Date().getFullYear()} AgentFlow Ventures Ltd. All rights reserved. Incorporated in the Cayman Islands.
            </p>
            <p className="text-xs text-slate-500">
              Informational compliance tooling — not legal advice.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function CounterTile({ label, value, recent, recentLabel, accent = "blue", note, divider, testId }) {
  const dotColor = accent === "cyan" ? "bg-cyan-500" : "bg-blue-600";
  const pingColor = accent === "cyan" ? "bg-cyan-400" : "bg-blue-400";
  const recentText =
    typeof recent === "number" && recentLabel ? recentLabel(recent) : null;
  return (
    <div
      className={`p-6 sm:p-7 ${divider ? "sm:border-l border-slate-200" : ""}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">
        <span className="relative flex h-2 w-2">
          <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${pingColor} opacity-75`} />
          <span className={`relative inline-flex h-2 w-2 rounded-full ${dotColor}`} />
        </span>
        Live
        <span className="text-slate-300">·</span>
        <span className="normal-case tracking-normal text-slate-500 font-medium">
          {label}
        </span>
      </div>
      <div
        className="mt-3 font-display font-semibold text-4xl sm:text-5xl tracking-tight text-slate-900 tabular-nums"
        data-testid={`${testId}-value`}
      >
        {value === null ? (
          <span className="inline-block h-10 w-24 bg-slate-100 rounded animate-pulse align-middle" />
        ) : (
          value.toLocaleString()
        )}
      </div>
      {recentText !== null && (
        <p
          className="mt-2 text-sm text-slate-700 font-medium"
          data-testid={`${testId}-recent`}
        >
          <span className={`inline-block h-1.5 w-1.5 rounded-full ${dotColor} mr-2 align-middle`} />
          {recentText}
        </p>
      )}
      {note && (
        <p className="mt-1 text-xs text-slate-500 leading-relaxed">{note}</p>
      )}
    </div>
  );
}


function FeatureCard({ icon, title, body, wide, children, testId, label }) {
  return (
    <div
      className={`relative rounded-2xl border border-slate-200 bg-white p-7 shadow-sm hover:shadow-md transition-shadow ${
        wide ? "md:col-span-2" : ""
      }`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div className="h-10 w-10 rounded-lg bg-blue-50 text-blue-600 inline-flex items-center justify-center">
          {icon}
        </div>
        {label && (
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-blue-700 bg-blue-50 rounded-full px-2.5 py-1">
            {label}
          </span>
        )}
      </div>
      <h3 className="mt-5 font-display text-xl font-semibold text-slate-900 tracking-tight">
        {title}
      </h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
      {children}
    </div>
  );
}

function PricingCard({
  name,
  price,
  cadence,
  description,
  features,
  ctaLabel,
  ctaTestId,
  onClick,
  featured,
  testId,
  badge,
}) {
  return (
    <div
      data-testid={testId}
      className={`relative rounded-2xl p-8 flex flex-col ${
        featured
          ? "bg-slate-900 text-white border border-slate-800 shadow-xl"
          : "bg-white border border-slate-200 shadow-sm"
      }`}
    >
      {featured && (
        <span className="absolute -top-3 left-8 inline-flex items-center gap-1 rounded-full bg-blue-600 text-white text-xs font-semibold px-3 py-1 shadow-sm">
          <Sparkles className="h-3 w-3" /> Most popular
        </span>
      )}
      {badge && (
        <span
          className={`absolute -top-3 right-8 inline-flex items-center rounded-full text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 shadow-sm ${
            featured ? "bg-emerald-500 text-white" : "bg-blue-50 text-blue-700 border border-blue-200"
          }`}
          data-testid={`${testId}-badge`}
        >
          {badge}
        </span>
      )}
      <div className="flex items-baseline justify-between">
        <h3
          className={`font-display text-xl font-semibold tracking-tight ${
            featured ? "text-white" : "text-slate-900"
          }`}
        >
          {name}
        </h3>
        {featured && (
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-cyan-300">
            Pro
          </span>
        )}
      </div>
      <p
        className={`mt-2 text-sm leading-relaxed ${
          featured ? "text-slate-300" : "text-slate-600"
        }`}
      >
        {description}
      </p>
      <div className="mt-6 flex items-baseline gap-1.5">
        <span
          className={`font-display text-5xl font-semibold tracking-tight ${
            featured ? "text-white" : "text-slate-900"
          }`}
        >
          {price}
        </span>
        <span className={`text-sm ${featured ? "text-slate-400" : "text-slate-500"}`}>
          / {cadence}
        </span>
      </div>
      <ul className="mt-7 space-y-3 flex-1">
        {features.map((f) => (
          <li
            key={f}
            className={`flex items-start gap-3 text-sm ${
              featured ? "text-slate-200" : "text-slate-700"
            }`}
          >
            <span
              className={`mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full shrink-0 ${
                featured ? "bg-blue-500/20 text-cyan-300" : "bg-blue-50 text-blue-600"
              }`}
            >
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            {f}
          </li>
        ))}
      </ul>
      <Button
        onClick={onClick}
        data-testid={ctaTestId}
        className={`mt-8 w-full h-11 rounded-lg text-sm font-semibold ${
          featured
            ? "bg-white text-slate-900 hover:bg-slate-100"
            : "bg-blue-600 text-white hover:bg-blue-700"
        }`}
      >
        {ctaLabel}
      </Button>
    </div>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-900">
        {title}
      </h4>
      <ul className="mt-4 space-y-2.5">
        {links.map((l) => {
          const item = typeof l === "string" ? { label: l, href: "#" } : l;
          return (
            <li key={item.label}>
              {item.to ? (
                <Link
                  to={item.to}
                  className="text-sm text-slate-600 hover:text-blue-600 transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  href={item.href}
                  className="text-sm text-slate-600 hover:text-blue-600 transition-colors break-all"
                >
                  {item.label}
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}


/* ================== Hero: Live-scan contract mock ==================
   Sits in the right column of the hero. Simulates a contract page being
   scanned in real time — red highlights over "dangerous" phrases with a
   floating risk-score badge. No animation library needed; pure CSS. */
function ScannedContractMock() {
  return (
    <div className="relative" data-testid="hero-contract-mock">
      {/* soft ambient wash behind the card */}
      <div className="absolute -inset-8 bg-gradient-to-tr from-blue-100 via-white to-slate-100 rounded-[2rem] blur-2xl opacity-70 -z-10" />

      {/* Contract page — deliberately looks like a physical document */}
      <div className="relative rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-slate-900/10 overflow-hidden">
        {/* Fake window chrome */}
        <div className="flex items-center gap-1.5 px-4 py-3 border-b border-slate-200 bg-slate-50/60">
          <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          <div className="ml-3 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">
            <FileText className="h-3 w-3" />
            services-agreement.pdf
          </div>
        </div>

        {/* "Contract" body */}
        <div className="px-6 pt-6 pb-8 font-serif text-[13px] leading-[1.75] text-slate-700 select-none">
          <div className="text-center font-bold text-slate-900 text-sm tracking-tight mb-4">
            MASTER SERVICES AGREEMENT
          </div>
          <p className="mb-3">
            <span className="font-semibold text-slate-900">3.2 Term & Renewal.</span>{" "}
            This Agreement shall{" "}
            <mark className="bg-rose-100 text-rose-900 font-medium px-1 rounded-sm">
              automatically renew for successive 12-month periods
            </mark>{" "}
            unless Client provides 90 days written notice prior to renewal.
          </p>
          <p className="mb-3">
            <span className="font-semibold text-slate-900">7.1 Fees.</span>{" "}
            Provider reserves the right to{" "}
            <mark className="bg-rose-100 text-rose-900 font-medium px-1 rounded-sm">
              modify fees at any time with 7 days notice
            </mark>{" "}
            at its sole discretion.
          </p>
          <p>
            <span className="font-semibold text-slate-900">9.4 Indemnification.</span>{" "}
            Client agrees to{" "}
            <mark className="bg-rose-100 text-rose-900 font-medium px-1 rounded-sm">
              indemnify and hold harmless against any and all claims
            </mark>{" "}
            arising from Client's use of the Service.
          </p>
        </div>
      </div>

      {/* Floating risk-score card — bottom-left */}
      <div
        className="hidden sm:flex absolute -bottom-6 -left-8 items-center gap-3 rounded-xl bg-white border border-slate-200 shadow-xl shadow-slate-900/10 px-4 py-3"
        data-testid="hero-risk-score-card"
      >
        <div className="relative h-11 w-11 shrink-0">
          <svg className="h-11 w-11 -rotate-90" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15.5" fill="none" stroke="#fee2e2" strokeWidth="3" />
            <circle
              cx="18" cy="18" r="15.5" fill="none"
              stroke="#e11d48" strokeWidth="3" strokeLinecap="round"
              strokeDasharray="97" strokeDashoffset="24"
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-[11px] font-black text-rose-600 tabular-nums">
            83
          </span>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-rose-600">
            Critical risk
          </div>
          <div className="text-xs font-semibold text-slate-900 whitespace-nowrap">
            3 dangerous clauses found
          </div>
        </div>
      </div>

      {/* Floating "AI scanning" badge — top-right */}
      <div className="hidden sm:flex absolute -top-4 -right-4 items-center gap-2 rounded-full bg-slate-900 text-white px-3 py-1.5 shadow-lg">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
        </span>
        <span className="text-[11px] font-semibold tracking-wide">
          AI scanning · 8s
        </span>
      </div>
    </div>
  );
}


/* ================== Social Proof Band ==================
   Dark surface between hero and Scary Contracts. Big live counter (from
   /api/risk-score/count), "As seen in" placeholder press logos, and
   trust badges (SOC 2 / GDPR / ISO). Breaks the page flow visually. */
function SocialProofBand({ reportsCount, reports24h }) {
  const bigNumber =
    reports24h !== null && reports24h >= 10
      ? reports24h
      : reportsCount !== null && reportsCount >= 10
      ? reportsCount
      : null;
  const bigNumberLabel =
    reports24h !== null && reports24h >= 10
      ? "risk reports generated this month"
      : "risk reports generated since launch";

  return (
    <section
      className="relative bg-slate-950 text-white py-20 sm:py-24 overflow-hidden"
      data-testid="social-proof-band"
    >
      {/* Subtle grain / noise overlay to avoid digital flatness */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 25% 30%, rgba(59,130,246,0.6) 0, transparent 50%), radial-gradient(circle at 80% 70%, rgba(34,211,238,0.35) 0, transparent 45%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left: massive live counter */}
          <div className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.28em] font-semibold text-blue-300">
              Live · updates every 30s
            </p>
            <div className="mt-4 flex items-baseline gap-3">
              <span
                className="font-display font-black text-6xl sm:text-7xl lg:text-8xl tracking-tighter tabular-nums"
                data-testid="social-proof-big-counter"
              >
                {bigNumber === null ? (
                  <span className="inline-block h-16 w-40 bg-white/10 rounded-lg animate-pulse" />
                ) : (
                  bigNumber.toLocaleString()
                )}
              </span>
              {bigNumber !== null && (
                <TrendingUp className="h-8 w-8 text-emerald-400" />
              )}
            </div>
            <p className="mt-3 text-lg text-slate-300 max-w-md leading-relaxed">
              {bigNumberLabel} — from founders scanning contracts before they sign.
            </p>
          </div>

          {/* Right: press logos + trust badges */}
          <div className="lg:col-span-6 space-y-10">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-400">
                As seen in
              </p>
              <div
                className="mt-5 grid grid-cols-2 sm:grid-cols-4 items-center gap-x-8 gap-y-6"
                data-testid="as-seen-in-logos"
              >
                {["TECHCRUNCH", "PRODUCT HUNT", "HACKER NEWS", "INDIE HACKERS"].map(
                  (name) => (
                    <div
                      key={name}
                      className="text-slate-500 hover:text-slate-200 transition-colors duration-200 font-display font-bold tracking-[0.14em] text-xs"
                      data-testid={`press-logo-${name.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      {name}
                    </div>
                  ),
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {["SOC 2 Type II", "GDPR-ready", "ISO 27001 ready"].map((b) => (
                <span
                  key={b}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-[11px] font-semibold text-slate-200 tracking-wide"
                  data-testid={`trust-badge-${b.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


/* ================== Scary Contracts Showcase ==================
   3 real-looking "bad clause" cards, each with a risk-score badge and
   the exact scary snippet highlighted in red. Ends with a big CTA
   pushing to /register so the "oh no" reaction converts. */
function ScaryContractsShowcase({ onCtaClick }) {
  const cards = [
    {
      score: 92,
      severity: "CRITICAL",
      severityClass: "bg-rose-100 text-rose-800 ring-rose-200",
      ringClass: "stroke-rose-600",
      scoreClass: "text-rose-600",
      title: "Auto-renewal in perpetuity",
      docType: "Master Services Agreement · Section 3.2",
      snippet: (
        <>
          "This Agreement shall{" "}
          <mark className="bg-rose-100 text-rose-900 font-semibold px-1 rounded-sm">
            automatically renew for successive 12-month periods
          </mark>{" "}
          unless Client provides ninety (90) days written notice prior to renewal."
        </>
      ),
      findings: [
        "12-month lock-in — no early exit clause",
        "Notice period 3× industry standard",
        "No cap on renewal price increases",
      ],
    },
    {
      score: 87,
      severity: "CRITICAL",
      severityClass: "bg-rose-100 text-rose-800 ring-rose-200",
      ringClass: "stroke-rose-600",
      scoreClass: "text-rose-600",
      title: "Uncapped liability & indemnification",
      docType: "Vendor Agreement · Section 9.4",
      snippet: (
        <>
          "Client agrees to{" "}
          <mark className="bg-rose-100 text-rose-900 font-semibold px-1 rounded-sm">
            indemnify and hold harmless against any and all claims
          </mark>
          , without limitation, arising from use of the Service."
        </>
      ),
      findings: [
        "Unlimited financial exposure",
        "No mutual indemnification",
        "Applies to third-party IP claims",
      ],
    },
    {
      score: 78,
      severity: "HIGH",
      severityClass: "bg-amber-100 text-amber-800 ring-amber-200",
      ringClass: "stroke-amber-500",
      scoreClass: "text-amber-600",
      title: "Unilateral price changes",
      docType: "SaaS Terms · Section 7.1",
      snippet: (
        <>
          "Provider reserves the right to{" "}
          <mark className="bg-amber-100 text-amber-900 font-semibold px-1 rounded-sm">
            modify fees at any time with 7 days written notice
          </mark>{" "}
          at its sole discretion."
        </>
      ),
      findings: [
        "7-day notice below EU consumer law",
        "No cap on % increase",
        "Client cannot terminate on price change",
      ],
    },
  ];

  return (
    <section
      className="relative py-24 sm:py-32 bg-white"
      id="scary-contracts"
      data-testid="scary-contracts-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-rose-600">
            Real clauses. Real damage.
          </p>
          <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-[1.05]">
            The clauses{" "}
            <span className="italic font-light text-slate-500">most founders</span>{" "}
            never notice — until it's too late.
          </h2>
          <p className="mt-5 text-base text-slate-600 leading-relaxed">
            These are real patterns AgentFlow flagged in the last 30 days. Each one
            could cost a small business tens of thousands — and every one is buried
            deep in the fine print of contracts founders sign without reading.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="scary-contracts-grid">
          {cards.map((c, idx) => {
            const dashOffset = 100 - c.score;
            return (
              <article
                key={c.title}
                className="group relative rounded-2xl bg-white border border-slate-200 shadow-lg shadow-slate-900/5 hover:shadow-2xl hover:-translate-y-1 transition-transform duration-300 ease-out overflow-hidden"
                data-testid={`scary-card-${idx}`}
              >
                {/* Header: risk score ring + severity pill */}
                <div className="p-6 border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="relative h-14 w-14">
                        <svg className="h-14 w-14 -rotate-90" viewBox="0 0 36 36">
                          <circle cx="18" cy="18" r="15.5" fill="none"
                                  stroke="#f1f5f9" strokeWidth="3" />
                          <circle cx="18" cy="18" r="15.5" fill="none"
                                  strokeWidth="3" strokeLinecap="round"
                                  strokeDasharray="97"
                                  strokeDashoffset={97 * dashOffset / 100}
                                  className={c.ringClass} />
                        </svg>
                        <span className={`absolute inset-0 flex items-center justify-center text-sm font-black tabular-nums ${c.scoreClass}`}>
                          {c.score}
                        </span>
                      </div>
                      <div>
                        <span className={`inline-flex items-center gap-1 rounded-full ring-1 ring-inset px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${c.severityClass}`}>
                          <AlertTriangle className="h-3 w-3" />
                          {c.severity}
                        </span>
                        <div className="mt-1.5 text-xs text-slate-500">
                          {c.docType}
                        </div>
                      </div>
                    </div>
                  </div>
                  <h3 className="mt-4 font-display font-semibold text-lg text-slate-900 tracking-tight leading-snug">
                    {c.title}
                  </h3>
                </div>

                {/* Body: contract snippet */}
                <div className="px-6 py-5 bg-slate-50/50 font-serif text-[13px] leading-relaxed text-slate-700">
                  {c.snippet}
                </div>

                {/* Findings list */}
                <div className="px-6 py-5">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
                    AgentFlow flagged
                  </div>
                  <ul className="space-y-2">
                    {c.findings.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                        <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-rose-500 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>

        {/* CTA below the grid */}
        <div className="mt-14 flex flex-col items-center text-center">
          <p className="text-base text-slate-600 max-w-lg">
            AgentFlow reads the entire contract in ~10 seconds and{" "}
            <span className="font-semibold text-slate-900">
              tells you exactly what to push back on
            </span>{" "}
            — in plain English.
          </p>
          <Button
            onClick={onCtaClick}
            className="mt-6 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25 h-12 px-7 text-sm font-semibold transition-transform duration-200 hover:-translate-y-0.5"
            data-testid="scary-contracts-cta"
          >
            Upload Your First Contract — Free
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <p className="mt-3 text-xs text-slate-500">
            No credit card · 5 free scans / month
          </p>
        </div>
      </div>
    </section>
  );
}
